/**
 * Formato de montos y fechas del portal de cobranza.
 *
 * Vivía en `pages/admin/portal-cobranza/cuentaDetalleShared.tsx` (y otra copia en
 * `components/admin/portal-cobranza/StatusBadges.tsx`), así que componentes de fuera del portal
 * -el diálogo de evidencia, el panel del comprobante, la edición de cuenta- importaban desde una
 * página. `cuentaDetalleShared` lo sigue reexportando para no mover sus importadores de golpe.
 */

import { esImagenPorUrl } from '@/lib/tipoArchivoVisor';
import { parseFechaFlexible } from '@/lib/portal-cobranza/fechaBanco';

/**
 * `vacio` es el texto cuando no hay monto. El default `'-'` se queda por los consumidores de
 * fuera del portal (carga de evidencia, edición de cuenta); cobranza usa `fmtCurrencyCobranza`.
 */
export function fmtCurrency(n: number | null | undefined, vacio = '-'): string {
  if (n == null || isNaN(n)) return vacio;
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

/**
 * `2026-09-30` → `30 sept 2026`. Una fecha sin hora se toma local, sin corrimiento de zona.
 * Acepta también `20260930` (formato STP), `30/09/2026` y timestamps con o sin zona
 * (`lib/portal-cobranza/fechaBanco`). Lo que no se pueda leer sale como `vacio` (default "-" por
 * los consumidores de fuera del portal; cobranza usa `fmtDateCobranza`), nunca "Invalid Date".
 */
export function fmtDate(s: string | null | undefined, vacio = '-'): string {
  if (!s) return vacio;
  let d = parseFechaFlexible(s);
  if (!d) {
    const intento = new Date(s);
    d = Number.isNaN(intento.getTime()) ? null : intento;
  }
  if (!d) return vacio;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Texto del portal de cobranza para un monto o una fecha que no existe: nunca un guion. */
export const SIN_MONTO = 'Sin monto';
export const SIN_FECHA = 'Sin fecha';

/** `fmtCurrency` del portal de cobranza: un monto ausente se lee "Sin monto". */
export function fmtCurrencyCobranza(n: number | null | undefined): string {
  return fmtCurrency(n, SIN_MONTO);
}

/** `fmtDate` del portal de cobranza: una fecha ausente o ilegible se lee "Sin fecha". */
export function fmtDateCobranza(s: string | null | undefined): string {
  return fmtDate(s, SIN_FECHA);
}

/**
 * `YYYY-MM-DD` del día LOCAL. `toISOString()` da el día en UTC: en México, después de las 18:00
 * ya es mañana (pago manual con fecha de mañana, promesa que no deja elegir hoy).
 */
export function isoLocal(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function todayIso(): string {
  return isoLocal(new Date());
}

/** Por extensión, sin importar `?token=…` ni `#page=…` (ver `lib/tipoArchivoVisor`). */
export function isImage(url: string): boolean {
  return esImagenPorUrl(url);
}

/**
 * Periodo del selector (año, año + mes o día) a rango 'YYYY-MM-DD' para los filtros de fecha del
 * portal: elegir un periodo se vuelve su rango. Sin año no hay rango (null).
 */
export function rangoDePeriodo(v: { anio?: number | null; mes?: number | null; dia?: number | null }): { desde: string; hasta: string } | null {
  if (v.anio == null) return null;
  const dos = (n: number) => String(n).padStart(2, '0');
  if (v.mes == null) return { desde: `${v.anio}-01-01`, hasta: `${v.anio}-12-31` };
  const ult = new Date(v.anio, v.mes, 0).getDate();
  if (v.dia != null) { const d = `${v.anio}-${dos(v.mes)}-${dos(v.dia)}`; return { desde: d, hasta: d }; }
  return { desde: `${v.anio}-${dos(v.mes)}-01`, hasta: `${v.anio}-${dos(v.mes)}-${dos(ult)}` };
}
